/**
 * Primary Color SSOT (Single Source of Truth)
 *
 * Centralized utilities for accessing the primary/brand color from WordPress settings.
 * All components should use these utilities instead of accessing window.dddData directly.
 *
 * @see usePrimaryColor hook for React components
 */

import { blendColorWithOpacity } from '@/hooks/useThemeColor';

/** Default primary color used when WordPress setting is not available */
export const DEFAULT_PRIMARY_COLOR = '#4b2c78';

/**
 * Get the primary color from WordPress settings or fallback to default.
 * Use this in non-React code (e.g., Sentry). For React components, use usePrimaryColor hook.
 *
 * @returns Primary color in HEX format (e.g., '#4b2c78')
 */
export function getPrimaryColor(): string {
	return window.dddData?.primaryColor || DEFAULT_PRIMARY_COLOR;
}

/**
 * Convert HEX color to HSL values string (without hsl() wrapper).
 * Compatible with shadcn/ui CSS variables format.
 *
 * @param hex - HEX color (e.g., '#4b2c78' or '#abc')
 * @returns HSL values string (e.g., '263 47% 32%')
 *
 * @example
 * hexToHsl('#4b2c78') // Returns: '263 47% 32%'
 */
export function hexToHsl(hex: string): string {
	// Remove # if present
	hex = hex.replace('#', '');

	// Handle shorthand (e.g., #abc → #aabbcc)
	if (hex.length === 3) {
		hex = hex
			.split('')
			.map((c) => c + c)
			.join('');
	}

	// Parse RGB values
	const r = parseInt(hex.substring(0, 2), 16) / 255;
	const g = parseInt(hex.substring(2, 4), 16) / 255;
	const b = parseInt(hex.substring(4, 6), 16) / 255;

	// Find min and max
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;

	// Calculate lightness
	const l = (max + min) / 2;

	// Calculate saturation
	let s = 0;
	if (delta !== 0) {
		s = delta / (1 - Math.abs(2 * l - 1));
	}

	// Calculate hue
	let h = 0;
	if (delta !== 0) {
		if (max === r) {
			h = 60 * (((g - b) / delta) % 6);
		} else if (max === g) {
			h = 60 * ((b - r) / delta + 2);
		} else {
			h = 60 * ((r - g) / delta + 4);
		}
	}

	// Ensure hue is positive
	if (h < 0) {
		h += 360;
	}

	// Round values
	h = Math.round(h);
	s = Math.round(s * 100);
	const lPercent = Math.round(l * 100);

	// Return shadcn-compatible format: "h s% l%"
	return `${h} ${s}% ${lPercent}%`;
}

/**
 * Generate a color palette from primary color for charts.
 * Creates 6 variations by blending with white at different opacities.
 *
 * @param primaryColor - Base HEX color (e.g., '#4b2c78')
 * @returns Array of 6 HEX colors from full to lightest
 */
export function generateChartPalette(primaryColor: string): string[] {
	return [
		primaryColor, // Primary - full color
		blendColorWithOpacity(primaryColor, 0.75, '#ffffff'), // 75% - slightly lighter
		blendColorWithOpacity(primaryColor, 0.55, '#ffffff'), // 55% - medium light
		blendColorWithOpacity(primaryColor, 0.4, '#ffffff'), // 40% - light
		blendColorWithOpacity(primaryColor, 0.25, '#ffffff'), // 25% - very light
		blendColorWithOpacity(primaryColor, 0.15, '#ffffff'), // 15% - ultra light
	];
}
